import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { Pharmacy } from "@/Common/Api/Pharmacy/Dto/Responses/Pharmacy";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { LabSearchAutocomplete } from "@/Common/Components/Search/LabSearchAutocomplete";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { LabFacilityModel } from "@/Common/Models/Lab/LabFacilityModel";
import { FaxSignPrintExportableFileDefaultContext } from "@/ExportableFile/Components/FaxSignPrintExportableFileComponent";
import { UpdateFaxContactInfoDecorator } from "@/ExportableFile/Models/decorators/SetFaxContactInfoDecorator";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { AddEditLabPopup } from "./AddEditLabPopup";

interface IFaxInformationProps<T extends ExportableFile, TDefaultContent extends FaxSignPrintExportableFileDefaultContext> {
    parentId?: string;
    patientContactId?: string;
    onDataChange: (isValid: boolean, description: string, faxNumber: string, exportableFile?: T, phoneNumber?: string) => void;
    shouldFocusRecipientOnMount?: boolean;
    exportableFile?: T;
    isSending?: boolean;
    defaultContent?: TDefaultContent;
    customPharmacy?: Pharmacy;
}

class FaxAndSignatureLabRequestSelectionState {
    public customLab: LabFacilityModel;
    public customLabBeeingEdited: LabFacilityModel;
    public isSending: boolean;
    public isAddEditCustomLabPopupOpen: boolean;
    public availableCountryLookupIds: FlatLookupView[];

    public constructor(init?: Partial<FaxAndSignatureLabRequestSelectionState>) {
        Object.assign(this, init);
    }

    public static getDefault(): FaxAndSignatureLabRequestSelectionState {
        return new FaxAndSignatureLabRequestSelectionState({
            customLab: new LabFacilityModel(null),
            customLabBeeingEdited: new LabFacilityModel(null),
            isSending: false,
            isAddEditCustomLabPopupOpen: false,
            availableCountryLookupIds: []
        });
    }
}

class FaxAndSignatureLabRequestSelection extends BaseReactComponent<IFaxInformationProps<ExportableFile, Pharmacy>, FaxAndSignatureLabRequestSelectionState> {
    constructor(props: IFaxInformationProps<ExportableFile, Pharmacy>) {
        super(props);
        this.state = new FaxAndSignatureLabRequestSelectionState({
            ...FaxAndSignatureLabRequestSelectionState.getDefault(),
            availableCountryLookupIds: cacheStore.GetCountryLookups().filter(x => x.LookupCode == "CNCANADA")
        });
    }

    public componentDidMount(): void {
        this.setOverlay("FaxAndSignatureLabRequestSelectionOverlay");
    }

    public render(): JSX.Element {
        var customLab = (
            <div className="flex-container m ">
                {this.state.isAddEditCustomLabPopupOpen && (
                    <AddEditLabPopup
                        onSaveCallBack={lab => this.mutateState(s => s.customLab = lab)}
                        model={this.state.customLabBeeingEdited}
                        closePopupCallback={() => this.mutateState(s => s.isAddEditCustomLabPopupOpen = false)}
                        parentId={this.props.parentId}
                    />
                )}
                <div className="custom-clinician-table " onClick={e => e.stopPropagation()}>
                    <table>
                        <tbody>
                            <tr>
                                <td className="col1">
                                    <span className="float-right">{cacheStore.GetString("NameLabel")}</span>
                                </td>
                                <td className="col2">
                                    <LabSearchAutocomplete
                                        debounceOnTextChangeMS={400}
                                        componentId="custom_lab"
                                        searchOnMount={true}
                                        defaultText={!IsNullOrWhiteSpace(this.state.customLab.Model.Name) ? this.state.customLab.Model.Name : ""}
                                        onItemSelectCallback={lab => this.mutateState(s => s.customLab = lab)}
                                        onEditClickCallBack={labFacilityModel => this.mutateState(s => { s.isAddEditCustomLabPopupOpen = true; s.customLabBeeingEdited = labFacilityModel.Clone(); })}
                                        focusOnMount={this.props.shouldFocusRecipientOnMount}
                                        onTextChangedFunction={item => this.mutateCustomLabState(s => s.Model.Name = item)}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td className="col1">
                                    <span className="float-right">{cacheStore.GetString("CountryLabel")}</span>
                                </td>
                                <td className="col2">
                                    <select onChange={e => this.onCountryChange(e)} style={{ "minWidth": "290px", "maxWidth": "500px", "overflow": "hidden" }} className="form-control myle-control" value={this.state.customLab.countryMyleLookupId}>
                                        {this.state.availableCountryLookupIds.map((z: FlatLookupView) => (
                                            <option key={z.LookupID} value={z.LookupID}>{cacheStore.GetLocalizedValueByLookupId(z.LookupID)}</option>
                                        ))}
                                    </select>
                                </td>
                            </tr>
                            <tr>
                                <td className="col1">
                                    <span className="float-right">{cacheStore.GetString("FaxLabel")}</span>
                                </td>
                                <td className="col2">
                                    <TextboxMaskedControl
                                        style={{ "width": "100%" }}
                                        isValid={this.state.customLab.isFaxValid}
                                        allowEmpty={false}
                                        id="fax_faxSignatureLab"
                                        value={this.state.customLab.Model.Fax}
                                        onChangeFunction={(unmasked, masked) => this.mutateCustomLabState(customLab => customLab.SetFax(unmasked, masked))}
                                        item={null}
                                        mask={this.state.customLab.GetPhoneValidationRule().EditMaskPattern}
                                        validationErrorMessage=""
                                        debounceMs={300}
                                    />
                                </td>
                            </tr>
                            {this.state.customLab.Model.Address && (
                                <tr style={{ "paddingTop": "30px" }}>
                                    <td className="col1" style={{ "verticalAlign": "top" }}>
                                        <span className="float-right">{cacheStore.GetString("AddressLabel")}</span>
                                    </td>
                                    <td className="col2">
                                        <div style={{ width: "100%" }}><p>{this.state.customLab.Model.Address}  {(this.state.customLab.Model.Address && this.state.customLab.Model.RegionNumber) && <span>-Reg</span>}{this.state.customLab.Model.RegionNumber} </p></div>
                                        <div />
                                        {this.state.customLab.Model.City}
                                        <div />
                                        {this.state.customLab.Model.Province.length > 0 && cacheStore.GetLocalizedValueByLookupId(this.state.customLab.Model.Province)} {this.state.customLab.Model.PostalCode}
                                    </td>
                                </tr>
                            )}
                            {this.state.customLab.Model.Phone && (
                                <tr>
                                    <td className="col1">
                                        <span className="float-right">{cacheStore.GetString("TelephoneLabel")}</span>
                                    </td>
                                    <td className="col2">
                                        <span style={{ "width": "100%" }}>{MyleHelper.FormatPhone(this.state.customLab.Model.Phone, this.state.customLab.countryMyleLookupCode)}</span>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="tbc text-right" style={{ width: "100px", float: "left", marginTop: "5px", paddingRight: "15px" }}>
                    <span>
                        <a style={{ marginLeft: "5px" }} onClick={() => this.mutateState(s => { s.isAddEditCustomLabPopupOpen = true; s.customLabBeeingEdited = new LabFacilityModel(null); })}>{cacheStore.GetString("AddLabel")}</a>
                    </span>
                </div>
            </div>
        );

        return <div>{customLab}</div>;
    }

    public mutateState(mutation: (state: FaxAndSignatureLabRequestSelectionState) => void): void {
        super.mutateState(
            s => mutation(s),
            () => {
                const decoratedExportableFile = new UpdateFaxContactInfoDecorator(this.props.exportableFile);
                const customLab = this.state.customLab;

                this.props.onDataChange(
                    customLab.isFaxValid,
                    customLab.Model.Name,
                    customLab.GetFormattedFax(),
                    decoratedExportableFile.updateFaxContactInfo(customLab.Model.Name, customLab.GetFormattedFax()),
                    customLab.Model.Phone
                );
            }
        );
    }

    private mutateCustomLabState(mutation: (l: LabFacilityModel) => void): void {
        this.mutateState(s => mutation(s.customLab));
    }

    private onCountryChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        let countryMyleLookupId = e.target.value;

        this.mutateCustomLabState(lab => {
            lab.SetCountryByMyleLookupId(countryMyleLookupId);
            lab.SetDefaultProvince();
        });
    }
}

export { FaxAndSignatureLabRequestSelection, IFaxInformationProps };
